import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import StateObserver from 'src/StateObserver';
import {
  getStreakBattleRoyaleActiveTheme,
  getStreakBattleRoyaleStatus,
  isStreakBattleRoyaleActive,
  isStreakBattleRoyaleEnabled,
  isStreakBattleRoyaleRewarded,
} from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import { track } from 'src/lib/analytics/utils';
import { resolvePromoGroupFeature } from 'src/game/promo/promo.launch';

type OriginFeature = 'hud' | 'launch' | 'postPlay';

/** Open streak event intro popup. */
export async function openStreakBattleRoyaleIntro(
  {
    hideClose = false,
    originFeature,
  }: { hideClose?: boolean; originFeature?: OriginFeature } = {
    hideClose: false,
  },
) {
  const { user } = StateObserver.getState();

  // mark the intro as seen, if not already.
  if (!user.streak!.introSeen) {
    await StateObserver.invoke.setStreakBattleRoyaleIntroSeen();
  }

  return openPopupPromise('popupStreakBattleRoyaleIntroV2', {
    hideClose,
    origin: mapFeatureToDialogOrigin(originFeature),
  });
}

/**
 * Open streak event matchmaking popup.
 * Also resets the main popup's checkpoint cache to allow for a fresh start.
 */
export async function openStreakBattleRoyaleMatchmaking(
  originFeature?: OriginFeature,
) {
  return openPopupPromise('popupStreakBattleRoyaleMatchmakingV2', {
    origin: mapFeatureToDialogOrigin(originFeature),
  });
}

/**
 * Open streak event popup, if the event is active and not completed.
 */
export async function openStreakBattleRoyalePopup(
  originFeature: OriginFeature,
) {
  const { user } = StateObserver.getState();
  const now = StateObserver.now();
  const popupId = 'popupStreakBattleRoyaleV2';
  const popupOrigin = mapFeatureToDialogOrigin(originFeature);

  // skip if the event is not enabled or already completed and rewarded.
  if (!isStreakBattleRoyaleEnabled(user) || isStreakBattleRoyaleRewarded(user))
    return false;

  // Track the fuji quest event open
  track('StreakQuestOpen', {
    popupId,
    popupOrigin,
    originFeature,
    theme: getStreakBattleRoyaleActiveTheme(user),
    progression: user.streak?.progression ?? 'basic',
  });

  /**
   * Show the intro popup if the user hasn't seen it before
   * and event is not already active (ie. restarting straight from an expired state),
   * or the event is in an 'available' state,
   *
   */
  if (
    (!isStreakBattleRoyaleActive(user, now) && !user.streak!.introSeen) ||
    getStreakBattleRoyaleStatus(user) === 'available'
  ) {
    const proceed = await openStreakBattleRoyaleIntro({ originFeature });

    // The intro itself satisfies the recurring promo for this daily cycle.
    await resolvePromoGroupFeature({
      groupId: 'recurringFeatures',
      itemId: 'streakQuest',
    });

    // If the user closed the intro popup, don't show the event popup.
    if (!proceed) return false;

    // If not already joined, join the event.
    if (getStreakBattleRoyaleStatus(user) === 'available') {
      await StateObserver.invoke.joinStreakBattleRoyale();

      // Show the fake matchmaking sequence
      await openStreakBattleRoyaleMatchmaking(originFeature);
    }
  }

  // show the event popup.
  await openPopupPromise(popupId, {
    origin: popupOrigin,
  });

  // The main popup also satisfies the recurring promo when no intro appeared.
  await resolvePromoGroupFeature({
    groupId: 'recurringFeatures',
    itemId: 'streakQuest',
  });

  return true;
}

/** Open streak event retry popup. */
export async function openStreakBattleRoyaleRetry() {
  return openPopupPromise('popupStreakBattleRoyaleRetryV2', {});
}

/** Open streak event reward popup. */
export async function openStreakBattleRoyaleReward() {
  return openPopupPromise('popupStreakBattleRoyaleRewardV2', {});
}

export async function streakBattleRoyalePostGame() {
  const { user } = StateObserver.getState();
  // Skip if the event is not enabled.
  if (!isStreakBattleRoyaleEnabled(user)) return false;
  const status = getStreakBattleRoyaleStatus(user);
  // Skip if the event is not active, or doesn't have anything to resolve.
  if (!status || status === 'available' || status === 'rewarded') return false;
  return openStreakBattleRoyalePopup('postPlay');
}
